import { useCallback, useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PlateBookPanel } from "@/components/PlateBookPanel";

const media = (p) => `${API}/events/media/${p}?auth=${encodeURIComponent(localStorage.getItem("sv_token") || "")}`;
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
const tm = (iso) => new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });
const STATUS = { ok: ["READ", "#30D158"], review: ["NEEDS REVIEW", "#FF9F0A"], no_plate: ["NO PLATE VISIBLE", "#71717A"], reading: ["READING…", "#0A84FF"] };

function Stat({ label, value, tid }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={tid}>
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className="text-2xl font-display font-black">{value}</div>
    </div>
  );
}

function PlateRow({ r, onZoom, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(r.plate || "");
  const [lab, col] = STATUS[r.status] || STATUS.reading;
  const save = async () => {
    try { await api.patch(`/avstone/plates/${r.id}`, { plate: val }); toast.success("Plate saved"); setEdit(false); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const del = async () => { await api.delete(`/avstone/plates/${r.id}`); onChanged(); };
  return (
    <div className="grid grid-cols-[150px_1fr] sm:grid-cols-[220px_1fr_auto] gap-4 border border-white/10 p-3 items-center" data-testid={`plate-row-${r.id}`}>
      <button onClick={() => r.images?.[0] && onZoom(r)} className="aspect-video bg-black overflow-hidden" data-testid={`plate-photo-${r.id}`}>
        {r.images?.[0] ? <img src={media(r.images[0])} alt="vehicle" className="w-full h-full object-contain" /> : <span className="text-xs text-[#71717A]">no photo</span>}
      </button>
      <div className="space-y-1 min-w-0">
        {edit ? (
          <div className="flex gap-2">
            <input value={val} onChange={(e) => setVal(e.target.value.toUpperCase())} className="bg-black border border-white/20 px-2 py-1 font-mono-data text-lg w-40" data-testid={`plate-edit-input-${r.id}`} />
            <button onClick={save} className="px-3 bg-[#30D158] text-black text-xs font-bold" data-testid={`plate-edit-save-${r.id}`}>Save</button>
          </div>
        ) : (
          <div className="font-mono-data text-2xl font-black tracking-widest" data-testid={`plate-number-${r.id}`}>{r.plate || "—"} <span className="text-xs text-[#A1A1AA]">{r.state}</span></div>
        )}
        <div className="text-xs text-[#A1A1AA]">{[r.color, r.make, r.vehicle_type || r.vehicle].filter(Boolean).join(" · ")}{r.company_text ? ` · "${r.company_text}"` : ""}</div>
        <div className="text-xs font-mono-data text-[#71717A]">{tm(r.first_seen)}{r.last_seen !== r.first_seen ? `–${tm(r.last_seen)}` : ""} · {(r.cameras || [r.camera]).join(", ")}</div>
        <div className="flex gap-2 flex-wrap text-[10px] font-mono-data">
          <span style={{ color: col }} className="border border-current px-1.5 py-0.5" data-testid={`plate-status-${r.id}`}>{lab}{r.status !== "reading" && r.status !== "no_plate" ? ` ${r.confidence}%` : ""}</span>
          {r.status !== "no_plate" && r.status !== "reading" && (
            <span className={`px-1.5 py-0.5 border ${r.visit_no > 1 ? "border-[#FF9F0A] text-[#FF9F0A]" : "border-[#30D158] text-[#30D158]"}`} data-testid={`plate-visit-${r.id}`}>
              {r.visit_no > 1 ? `RETURNING · VISIT #${r.visit_no}` : "FIRST VISIT"}
            </span>
          )}
          {r.fleet && <span className="px-1.5 py-0.5 border border-[#0A84FF] text-[#0A84FF]">OWN FLEET {r.fleet_label}</span>}
          {r.customer && <span className="px-1.5 py-0.5 border border-[#BF5AF2] text-[#BF5AF2]" data-testid={`plate-customer-${r.id}`}>{r.customer}</span>}
        </div>
      </div>
      <div className="flex sm:flex-col gap-2 col-span-2 sm:col-span-1">
        <button onClick={() => setEdit(!edit)} className="px-3 py-1 border border-white/20 text-xs hover:bg-white/10 transition-colors" data-testid={`plate-edit-btn-${r.id}`}>{edit ? "Cancel" : "Correct"}</button>
        <button onClick={del} className="px-3 py-1 border border-[#FF3B30]/40 text-[#FF3B30] text-xs hover:bg-[#FF3B30]/10 transition-colors" data-testid={`plate-delete-btn-${r.id}`}>Remove</button>
      </div>
    </div>
  );
}

function FleetPanel({ onChanged }) {
  const [fleet, setFleet] = useState([]);
  const [plate, setPlate] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/avstone/fleet").then((r) => setFleet(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged(); };
  const add = async () => {
    try { await api.post("/avstone/fleet", { plate, label }); setPlate(""); setLabel(""); toast.success("Truck added — hidden from the log"); done(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
    try { const r = await api.post("/avstone/fleet/photo", { image_b64: b64, label }); toast.success(`Read plate ${r.data.plate} — added to fleet`); setLabel(""); done(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/avstone/fleet/${id}`); done(); };
  return (
    <div className="border border-white/10 p-4 space-y-3" data-testid="fleet-panel">
      <div>
        <h2 className="font-display font-black text-base">My trucks (hidden from the log)</h2>
        <p className="text-xs text-[#71717A]">Type a plate, or upload a photo of the truck — the AI reads the plate for you.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="PLATE e.g. P123456" className="bg-black border border-white/20 px-2 py-1.5 font-mono-data text-sm w-40" data-testid="fleet-plate-input" />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Name (e.g. Truck 3 - Isuzu)" className="bg-black border border-white/20 px-2 py-1.5 text-sm flex-1 min-w-[160px]" data-testid="fleet-label-input" />
        <button onClick={add} disabled={plate.length < 2} className="px-4 py-1.5 bg-white text-black text-xs font-bold disabled:opacity-40" data-testid="fleet-add-btn">Add plate</button>
        <label className={`px-4 py-1.5 border border-white/30 text-xs font-bold cursor-pointer hover:bg-white/10 transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="fleet-photo-btn">
          {busy ? "Reading photo…" : "Upload truck photo"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="fleet-photo-input" />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
        {fleet.map((f) => (
          <div key={f.id} className="flex items-center gap-3 border border-white/10 p-2" data-testid={`fleet-item-${f.id}`}>
            {f.photo_path ? <img src={media(f.photo_path)} alt="truck" className="w-16 h-10 object-cover" /> : <div className="w-16 h-10 bg-white/5" />}
            <div className="flex-1 min-w-0">
              <div className="font-mono-data font-black tracking-widest">{f.plate}</div>
              <div className="text-[11px] text-[#71717A] truncate">{f.label || (f.source === "photo" ? "from photo" : "typed")}</div>
            </div>
            <button onClick={() => del(f.id)} className="text-[#FF3B30] text-xs px-2" data-testid={`fleet-delete-${f.id}`}>Remove</button>
          </div>
        ))}
        {!fleet.length && <div className="text-xs text-[#71717A]" data-testid="fleet-empty">No trucks yet.</div>}
      </div>
    </div>
  );
}

export default function AvStonePlates() {
  const [date, setDate] = useState(today());
  const [tab, setTab] = useState("all");
  const [showFleet, setShowFleet] = useState(false);
  const [data, setData] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/avstone/plates", { params: { date, include_fleet: showFleet } })
    .then((r) => setData(r.data)).catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail))), [date, showFleet]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (err) return <div className="p-6 text-[#FF3B30] font-mono-data text-sm" data-testid="plates-error">{err}</div>;
  const rows = (data?.rows || []).filter((r) => tab === "all" || r.status === tab);
  const s = data?.stats || {};
  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="avstone-plates">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-2xl sm:text-3xl">A V Stone — Number Plates</h1>
          <p className="text-xs text-[#71717A]">Every vehicle the cameras see, with its sharpest photo and the plate read by AI. Your own trucks are hidden.</p>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-black border border-white/20 px-2 py-1.5 text-sm" data-testid="plates-date" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Vehicles" value={s.vehicles ?? "—"} tid="plates-stat-vehicles" />
        <Stat label="Returning" value={s.returning ?? "—"} tid="plates-stat-returning" />
        <Stat label="Needs review" value={s.review ?? "—"} tid="plates-stat-review" />
        <Stat label="No plate visible" value={s.no_plate ?? "—"} tid="plates-stat-noplate" />
        <Stat label="Own trucks hidden" value={s.fleet_hidden ?? "—"} tid="plates-stat-fleet" />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {[["all", "All"], ["review", "Needs review"], ["no_plate", "No plate"], ["ok", "Read"], ["book", "Customer plates"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1 text-xs border transition-colors ${tab === k ? "bg-white text-black border-white" : "border-white/20 hover:bg-white/10"}`} data-testid={`plates-tab-${k}`}>{l}</button>
        ))}
        <label className="ml-auto text-xs flex items-center gap-2 text-[#A1A1AA]">
          <input type="checkbox" checked={showFleet} onChange={(e) => setShowFleet(e.target.checked)} data-testid="plates-show-fleet" /> Show my trucks
        </label>
      </div>
      {tab === "book" ? <PlateBookPanel onChanged={load} /> : (
        <>
          <div className="space-y-2" data-testid="plates-list">
            {rows.map((r) => <PlateRow key={r.id} r={r} onZoom={setZoom} onChanged={load} />)}
            {data && !rows.length && <div className="text-sm text-[#71717A] border border-dashed border-white/10 p-6" data-testid="plates-empty">No vehicles logged for this day yet. Plates appear here once the A V Stone edge PC is running.</div>}
          </div>
          <FleetPanel onChanged={load} />
        </>
      )}
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 p-4 flex flex-col items-center justify-center gap-3" onClick={() => setZoom(null)} data-testid="plate-zoom">
          <div className="font-mono-data text-white text-xl tracking-widest">{zoom.plate || "—"} · click to close</div>
          <div className="flex gap-2 flex-wrap justify-center">
            {zoom.images.map((p) => <img key={p} src={media(p)} alt="vehicle" className="max-h-[75vh] max-w-[90vw] object-contain" />)}
          </div>
        </div>
      )}
    </div>
  );
}
